import React, {
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { Input, Modal, Popconfirm, Tooltip, notification } from 'antd'
import type { InputRef } from 'antd'
import {
    CheckOutlined,
    CopyOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from '@ant-design/icons'
import { useRouter } from 'next/router'
import { useRecoilValue } from 'recoil'
import { nanoid } from 'nanoid'
import styles from './EupheusConversations.module.less'
import { AiMarkdown } from './AiMarkdown'
import { AiSparkleIcon } from '@/components/icons/AiSparkleIcon'
import { authStateAtom, frontendDisplayedCourseSelector } from '@/atoms/auth'
import { sectionTitleByHashedContentPathSelector } from '@/atoms/contentTreeDerived'
import { hashContentPath } from 'common/src/ContentPath'
import {
    deleteQaConversation,
    getQaConversation,
    listQaConversations,
    respondQaBrowserRequest,
    sendQaMessage,
    startQaConversation,
} from '@/api/cb/qa'
import {
    QaConversationPublicState,
    QaConversationStatus,
    QaConversationSummary,
    QaMessageRole,
    QaStage,
} from 'common/src/api/backendToBackend/qa/types'
import { executeAgentBrowserRequest } from '@/utils/agentBrowserRequests'
import { formatFullDateTime, formatHumanTime } from '@/utils/aiDateTime'

const POLL_INTERVAL_MS = 1_000

const stageText = (conversation: QaConversationPublicState): string => {
    if (conversation.status === QaConversationStatus.queued) {
        const position = (conversation.queuePosition ?? 0) + 1
        if (position > 1) return `You're #${position} in line…`
        const isFirstTurn =
            conversation.messages.filter(
                (message) => message.role === QaMessageRole.eupheus
            ).length === 0
        return isFirstTurn ? 'Getting set up…' : 'Reading your message…'
    }
    const entered = conversation.stageEvents.map((event) => event.stage)
    const latest = entered[entered.length - 1]
    if (latest === QaStage.writing || latest === QaStage.finalizing)
        return 'Writing a reply…'
    if (latest === QaStage.searching) return 'Searching the course content…'
    return 'Reading your message…'
}

// Creator conversations with Eupheus: floating button (bottom-right) on
// the content editor. Same machinery as the student version on WF, tuned
// for authors (editorial answers, editor deep links). Port of
// Website-Frontend's EupheusConversations — keep fixes in sync.
export const EupheusConversations: React.FC = (): ReactElement | null => {
    const authState = useRecoilValue(authStateAtom)
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const sectionTitles = useRecoilValue(
        sectionTitleByHashedContentPathSelector
    )
    const [isOpen, setIsOpen] = useState(false)
    const [summaries, setSummaries] = useState<QaConversationSummary[]>([])
    const [activeConversation, setActiveConversation] =
        useState<QaConversationPublicState | null>(null)
    const [isComposingNew, setIsComposingNew] = useState(false)
    const [draft, setDraft] = useState('')
    const [isSending, setIsSending] = useState(false)
    const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(
        null
    )
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<InputRef>(null)
    const router = useRouter()
    const handledBrowserRequests = useRef<Set<string>>(new Set())

    const refreshList = useCallback(async (): Promise<void> => {
        if (!courseName) return
        const response = await listQaConversations({ courseName })
        if (response.data.success)
            setSummaries(response.data.payload.conversations)
    }, [courseName])

    useEffect(() => {
        if (isOpen) void refreshList()
    }, [isOpen, refreshList])

    const isAnswering =
        activeConversation !== null &&
        activeConversation.status !== QaConversationStatus.idle

    // Poll while a turn is in flight; also execute agent → browser
    // requests (page snapshot / navigation) exactly once each.
    useEffect(() => {
        if (!isAnswering || !activeConversation || !courseName) return
        const conversationId = activeConversation.conversationId
        const timer = window.setInterval((): void => {
            void getQaConversation({ courseName, conversationId }).then(
                (response) => {
                    if (!response.data.success) return
                    const conversation = response.data.payload.conversation
                    setActiveConversation(conversation)
                    if (conversation.status === QaConversationStatus.idle)
                        void refreshList()
                    const browserRequest = conversation.browserRequest
                    if (
                        browserRequest &&
                        !handledBrowserRequests.current.has(
                            browserRequest.browserRequestId
                        )
                    ) {
                        handledBrowserRequests.current.add(
                            browserRequest.browserRequestId
                        )
                        void executeAgentBrowserRequest(
                            browserRequest,
                            router
                        ).then((browserResponse) =>
                            respondQaBrowserRequest({
                                courseName,
                                conversationId,
                                response: browserResponse,
                            })
                        )
                    }
                }
            )
        }, POLL_INTERVAL_MS)
        return () => window.clearInterval(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isAnswering,
        activeConversation?.conversationId,
        courseName,
        refreshList,
        router,
    ])

    // Narration debounce: hide sub-second blips.
    const NARRATION_DEBOUNCE_MS = 1_500
    const hasNarration = Boolean(
        isAnswering && activeConversation?.liveNarration
    )
    const [showNarration, setShowNarration] = useState<boolean>(false)
    useEffect(() => {
        if (!hasNarration) {
            setShowNarration(false)
            return
        }
        const timer = window.setTimeout(
            () => setShowNarration(true),
            NARRATION_DEBOUNCE_MS
        )
        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNarration, activeConversation?.conversationId])

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeConversation?.messages.length,
        isAnswering,
        activeConversation?.liveAnswer?.length,
        activeConversation?.liveNarration?.length,
    ])

    const inChat = activeConversation !== null || isComposingNew
    useEffect(() => {
        if (!inChat || isAnswering) return
        const timer = window.setTimeout(() => inputRef.current?.focus(), 50)
        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inChat, isAnswering, activeConversation?.conversationId])

    const openConversation = useCallback(
        async (conversationId: string): Promise<void> => {
            const response = await getQaConversation({
                courseName,
                conversationId,
            })
            if (response.data.success) {
                setActiveConversation(response.data.payload.conversation)
                setIsComposingNew(false)
            }
        },
        [courseName]
    )

    const send = useCallback(async (): Promise<void> => {
        const question = draft.trim()
        if (!question || isSending) return
        setIsSending(true)
        try {
            const response = activeConversation
                ? await sendQaMessage({
                      courseName,
                      conversationId: activeConversation.conversationId,
                      qaRequestId: nanoid(),
                      question,
                  })
                : await startQaConversation({
                      courseName,
                      qaRequestId: nanoid(),
                      question,
                  })
            if (response.data.success) {
                setActiveConversation(response.data.payload.conversation)
                setIsComposingNew(false)
                setDraft('')
            } else {
                notification.warning({
                    message: 'Message not sent',
                    description: response.data.error,
                })
            }
        } finally {
            setIsSending(false)
        }
    }, [draft, isSending, activeConversation, courseName])

    const copyMessage = useCallback(
        async (text: string, index: number): Promise<void> => {
            try {
                await navigator.clipboard.writeText(text)
                setCopiedMessageIndex(index)
                window.setTimeout(() => setCopiedMessageIndex(null), 1_500)
            } catch {
                notification.error({
                    message: 'Unable to copy',
                    description: 'Your browser blocked clipboard access.',
                })
            }
        },
        []
    )

    if (!authState || !courseName) return null

    const messages = activeConversation?.messages ?? []

    return (
        <>
            <Tooltip title={'Conversations with Eupheus'} placement={'left'}>
                <button
                    className={styles.fab}
                    onClick={() => setIsOpen(true)}
                    aria-label={'Conversations with Eupheus'}
                >
                    <img
                        className={styles.fabIcon}
                        src={'/eupheusChatFab.webp'}
                        alt={''}
                        draggable={false}
                    />
                </button>
            </Tooltip>
            <Modal
                zIndex={1010}
                open={isOpen}
                onCancel={() => setIsOpen(false)}
                footer={null}
                width={'min(1100px, 94vw)'}
                className={styles.conversationsModal}
                destroyOnClose={false}
            >
                {!inChat && (
                    <div className={styles.listView}>
                        <div className={styles.headerTitle}>
                            <AiSparkleIcon /> Conversations with Eupheus
                        </div>
                        <div className={styles.listHint}>
                            Ask about your course content: coverage,
                            consistency, sources, where things live — Eupheus
                            researches the actual materials before answering.
                        </div>
                        <button
                            className={styles.newConversationButton}
                            onClick={() => {
                                setActiveConversation(null)
                                setIsComposingNew(true)
                            }}
                        >
                            <AiSparkleIcon /> New conversation
                        </button>
                        {summaries.length > 0 && (
                            <div className={styles.conversationRows}>
                                {summaries.map((summary) => (
                                    <div
                                        key={summary.conversationId}
                                        className={styles.conversationRow}
                                    >
                                        <button
                                            className={styles.conversationOpen}
                                            onClick={() =>
                                                void openConversation(
                                                    summary.conversationId
                                                )
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.conversationTitle
                                                }
                                            >
                                                {summary.title ??
                                                    'New conversation'}
                                                {summary.status !==
                                                    QaConversationStatus.idle && (
                                                    <>
                                                        {' '}
                                                        <LoadingOutlined spin />
                                                    </>
                                                )}
                                            </span>
                                            <Tooltip
                                                title={formatFullDateTime(
                                                    summary.updatedAt
                                                )}
                                            >
                                                <span
                                                    className={
                                                        styles.conversationMeta
                                                    }
                                                >
                                                    {formatHumanTime(
                                                        summary.updatedAt
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </button>
                                        <Popconfirm
                                            title={'Delete this conversation?'}
                                            onConfirm={async () => {
                                                await deleteQaConversation({
                                                    courseName,
                                                    conversationId:
                                                        summary.conversationId,
                                                })
                                                void refreshList()
                                            }}
                                        >
                                            <button
                                                className={
                                                    styles.conversationDelete
                                                }
                                                aria-label={
                                                    'Delete conversation'
                                                }
                                            >
                                                <DeleteOutlined />
                                            </button>
                                        </Popconfirm>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {inChat && (
                    <div className={styles.chatView}>
                        <button
                            className={styles.backButton}
                            onClick={() => {
                                setActiveConversation(null)
                                setIsComposingNew(false)
                                void refreshList()
                            }}
                        >
                            ← All conversations
                        </button>
                        <div className={styles.chatTitle}>
                            {activeConversation?.title ?? 'New conversation'}
                        </div>
                        <div className={styles.chatMessages}>
                            {messages.map((message, index) =>
                                message.role === QaMessageRole.student ? (
                                    <div
                                        key={index}
                                        className={styles.creatorMessage}
                                    >
                                        {message.text}
                                        <div className={styles.messageTimeRow}>
                                            <button
                                                className={styles.copyButton}
                                                aria-label={'Copy message'}
                                                onClick={() =>
                                                    void copyMessage(
                                                        message.text,
                                                        index
                                                    )
                                                }
                                            >
                                                {copiedMessageIndex ===
                                                index ? (
                                                    <CheckOutlined />
                                                ) : (
                                                    <CopyOutlined />
                                                )}
                                            </button>
                                            <Tooltip
                                                title={formatFullDateTime(
                                                    message.at
                                                )}
                                            >
                                                <span
                                                    className={
                                                        styles.messageTime
                                                    }
                                                >
                                                    {formatHumanTime(
                                                        message.at
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        key={index}
                                        className={styles.eupheusMessage}
                                    >
                                        <div className={styles.eupheusLabel}>
                                            <AiSparkleIcon /> Eupheus
                                        </div>
                                        <AiMarkdown
                                            onNavigate={() => setIsOpen(false)}
                                        >
                                            {message.text}
                                        </AiMarkdown>
                                        {message.citedContentPaths.length >
                                            0 && (
                                            <div
                                                className={
                                                    styles.messageSources
                                                }
                                            >
                                                <span
                                                    className={
                                                        styles.sourcesLabel
                                                    }
                                                >
                                                    Sources:
                                                </span>
                                                {message.citedContentPaths.map(
                                                    (
                                                        contentPath,
                                                        sourceIndex
                                                    ) => {
                                                        const hashed =
                                                            hashContentPath(
                                                                contentPath
                                                            )
                                                        return (
                                                            <a
                                                                key={
                                                                    sourceIndex
                                                                }
                                                                href={`/dashboard?contentPath=${hashed}&viewingStateType=outline`}
                                                                onClick={(
                                                                    event
                                                                ) => {
                                                                    event.preventDefault()
                                                                    setIsOpen(
                                                                        false
                                                                    )
                                                                    void router.push(
                                                                        `/dashboard?contentPath=${hashed}&viewingStateType=outline`
                                                                    )
                                                                }}
                                                            >
                                                                {sectionTitles.get(
                                                                    hashed
                                                                ) ??
                                                                    'Open section'}
                                                            </a>
                                                        )
                                                    }
                                                )}
                                            </div>
                                        )}
                                        <div className={styles.messageTimeRow}>
                                            <button
                                                className={styles.copyButton}
                                                aria-label={'Copy message'}
                                                onClick={() =>
                                                    void copyMessage(
                                                        message.text,
                                                        index
                                                    )
                                                }
                                            >
                                                {copiedMessageIndex ===
                                                index ? (
                                                    <CheckOutlined />
                                                ) : (
                                                    <CopyOutlined />
                                                )}
                                            </button>
                                            <Tooltip
                                                title={formatFullDateTime(
                                                    message.at
                                                )}
                                            >
                                                <span
                                                    className={
                                                        styles.messageTime
                                                    }
                                                >
                                                    {formatHumanTime(
                                                        message.at
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                )
                            )}
                            {isAnswering && activeConversation && (
                                <div className={styles.eupheusMessage}>
                                    <div className={styles.eupheusLabel}>
                                        <AiSparkleIcon /> Eupheus
                                    </div>
                                    {activeConversation.liveAnswer ? (
                                        <div>
                                            <AiMarkdown>
                                                {activeConversation.liveAnswer}
                                            </AiMarkdown>
                                            <span
                                                className={styles.streamCursor}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            {activeConversation.liveNarration &&
                                                showNarration && (
                                                    <div
                                                        className={
                                                            styles.liveNarration
                                                        }
                                                    >
                                                        {
                                                            activeConversation.liveNarration
                                                        }
                                                        <span
                                                            className={
                                                                styles.streamCursor
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            <div className={styles.stageText}>
                                                <LoadingOutlined spin />{' '}
                                                <span
                                                    className={styles.shimmer}
                                                >
                                                    {activeConversation.liveActivity ??
                                                        stageText(
                                                            activeConversation
                                                        )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeConversation?.lastTurnFailed &&
                                !isAnswering && (
                                    <div className={styles.failureNote}>
                                        {activeConversation.failureMessage}
                                    </div>
                                )}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className={styles.chatInputRow}>
                            <Input.TextArea
                                ref={inputRef}
                                value={draft}
                                onChange={(event) =>
                                    setDraft(event.target.value)
                                }
                                onPressEnter={(event) => {
                                    if (event.shiftKey) return
                                    event.preventDefault()
                                    void send()
                                }}
                                placeholder={
                                    activeConversation
                                        ? 'Reply to Eupheus…'
                                        : 'Ask Eupheus about your course content…'
                                }
                                autoSize={{ minRows: 1, maxRows: 5 }}
                                disabled={isAnswering || isSending}
                            />
                            <button
                                className={styles.sendButton}
                                onClick={() => void send()}
                                disabled={
                                    !draft.trim() || isAnswering || isSending
                                }
                            >
                                Send
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    )
}
